/**
 * Helper functions for sidebar conversations transformation and filtering.
 */

import type { ConversationResponse } from '@saaga/shared/interfaces/conversation.interface'
import { MessageSquareIcon } from 'lucide-react'

import type { SidebarNavItem } from './sidebar-nav-item.interface'

export interface ConversationPinStateMap {
  [conversationId: string]: boolean
}

const isUUID = (str: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)

const buildConversationPath = (conversation: ConversationResponse): string => {
  if (conversation.agency_slug && !isUUID(conversation.agency_slug)) {
    return `/chat/${conversation.agency_slug}/${conversation.id}`
  }
  return `/c/${conversation.id}`
}

export const transformConversationToNavItem = (
  conversation: ConversationResponse
): SidebarNavItem => ({
  id: conversation.id,
  title: conversation.name,
  path: buildConversationPath(conversation),
  icon: MessageSquareIcon,
  itemType: 'chat',
  isPinned: conversation.is_pinned,
})

export const filterPinnedItems = (itemList: SidebarNavItem[]): SidebarNavItem[] =>
  itemList.filter((item) => item.isPinned)

export const filterUnpinnedItems = (itemList: SidebarNavItem[]): SidebarNavItem[] =>
  itemList.filter((item) => !item.isPinned)

export const buildConversationPinStateMap = (itemList: SidebarNavItem[]): ConversationPinStateMap =>
  itemList.reduce<ConversationPinStateMap>((pinMap, item) => {
    if (item.id) {
      pinMap[item.id] = item.isPinned ?? false
    }
    return pinMap
  }, {})

export const mergeUniqueItems = (
  existingItemList: SidebarNavItem[],
  newItemList: SidebarNavItem[]
): SidebarNavItem[] => {
  const existingIdSet = new Set(existingItemList.map((item) => item.id))
  const uniqueNewItemList = newItemList.filter((item) => !existingIdSet.has(item.id))
  return [...existingItemList, ...uniqueNewItemList]
}
